import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Sparkles, Bot, Shield, ArrowRight, CheckCircle2, Clock,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Search,
    Filter, Lock, Server, Users, ShieldAlert, Upload,
    BookOpen, Layers, GitBranch, Edit3, Trash2, UserCheck,
    HardDrive, Network, Database, Info, History, CheckCheck
} from "lucide-react";

export default function EnterpriseInsights() {
    const navigate = useNavigate();
    const [insightsData, setInsightsData] = useState({ metrics: {}, insights: [] });
    const [generatedInsights, setGeneratedInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("active");
    const [searchQuery, setSearchQuery] = useState("");
    const [toast, setToast] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingInsight, setEditingInsight] = useState(null);
    const [newInsightForm, setNewInsightForm] = useState({
        artifact: "",
        artifactType: "Users, Identities, and Roles",
        insight: "",
        condition: "Always active"
    });

    const [suggestions, setSuggestions] = useState([
        {
            id: "sug-01",
            artifact: "IronLink-SSLVPN-Gateway",
            artifactType: "Networks, Domains, and IP Addresses",
            observedPattern: "Observed 14,200 successful MFA sessions for remote employees between 08:00 and 19:00 EST.",
            recommendation: "Tag as Authorized Corporate Ingress Endpoint to suppress off-hours false-positive brute force alerts.",
            confidence: "98%",
            status: "pending"
        },
        {
            id: "sug-02",
            artifact: "svc_ad_sync@querytel.corp",
            artifactType: "Users, Identities, and Roles",
            observedPattern: "High-volume Kerberos TGS requests every 15 minutes directed to Primary Domain Controller.",
            recommendation: "Mark as Authorized Active Directory Synchronization Service to bypass credential dumping alerts.",
            confidence: "96%",
            status: "pending"
        },
        {
            id: "sug-03",
            artifact: "10.180.80.50",
            artifactType: "Networks, Domains, and IP Addresses",
            observedPattern: "Continuous syslog stream UDP 514 transmitting 600,000+ telemetry events daily.",
            recommendation: "Register as FortiAnalyzer Core Telemetry Collector in the institutional asset inventory.",
            confidence: "99%",
            status: "pending"
        },
        {
            id: "sug-04",
            artifact: "KingTravels-Corporate-LAN",
            artifactType: "Networks, Domains, and IP Addresses",
            observedPattern: "Inter-VLAN SMB (port 445) file transfers between internal financial file shares.",
            recommendation: "Classify as Internal Trusted Business VLAN with standard baseline throughput.",
            confidence: "95%",
            status: "pending"
        }
    ]);

    const [auditLog, setAuditLog] = useState([
        { id: "aud-01", action: "Fact Verified", target: "Querytel-DataCanter", user: "SecOps Lead (System Ingest)", time: "10 mins ago", status: "Active" },
        { id: "aud-02", action: "Policy Enforced", target: "BCS-80-WAN-FW-01", user: "Network Admin", time: "1 hour ago", status: "Active" },
        { id: "aud-03", action: "Rule Bound", target: "10.180.80.0/24 Subnet", user: "Infrastructure Ops", time: "3 hours ago", status: "Active" },
        { id: "aud-04", action: "Baseline Calculated", target: "HTTPS Port 443 Service", user: "QueryTel Engine", time: "1 day ago", status: "Active" },
        { id: "aud-05", action: "Access Verified", target: "SSL-VPN Gateway Ingress", user: "Identity Team", time: "2 days ago", status: "Active" }
    ]);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchInsights();
        fetchGeneratedInsights();
    }, []);

    const fetchInsights = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/insights`, { headers });
            setInsightsData(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load insights:", err);
        }
        setLoading(false);
    };

    const fetchGeneratedInsights = async () => {
        try {
            const res = await axios.get(`${backendURL}/api/insights/generated`, { headers });
            setGeneratedInsights(res.data.insights || []);
        } catch (err) {
            console.error("Failed to load generated insights:", err);
        }
    };

    const handleCreateInsight = async (e) => {
        e.preventDefault();
        try {
            if (editingInsight) {
                const res = await axios.put(`${backendURL}/api/insights/${editingInsight.id}`, newInsightForm, { headers });
                if (res.data.success) {
                    setInsightsData(prev => ({
                        ...prev,
                        insights: prev.insights.map(i => i.id === editingInsight.id ? res.data.insight : i)
                    }));
                    setModalOpen(false);
                    setEditingInsight(null);
                    setNewInsightForm({
                        artifact: "",
                        artifactType: "Users, Identities, and Roles",
                        insight: "",
                        condition: "Always active"
                    });
                    setToast("Environmental Fact Updated Successfully!");
                    setTimeout(() => setToast(null), 3000);
                }
            } else {
                const res = await axios.post(`${backendURL}/api/insights`, newInsightForm, { headers });
                if (res.data.success) {
                    setInsightsData(prev => ({
                        ...prev,
                        insights: [res.data.insight, ...prev.insights]
                    }));
                    setModalOpen(false);
                    setNewInsightForm({
                        artifact: "",
                        artifactType: "Users, Identities, and Roles",
                        insight: "",
                        condition: "Always active"
                    });
                    setToast("New Environmental Fact Added to QueryTel Context!");
                    setTimeout(() => setToast(null), 3000);
                }
            }
        } catch (err) {
            console.error("Failed to save insight:", err);
            setToast("Failed to save fact: " + (err.response?.data?.error || err.message));
            setTimeout(() => setToast(null), 3500);
        }
    };

    const openEditModal = (ins, e) => {
        if (e) e.stopPropagation();
        setEditingInsight(ins);
        setNewInsightForm({
            artifact: ins.artifact,
            artifactType: ins.artifactType,
            insight: ins.insight,
            condition: ins.condition || "Always active"
        });
        setModalOpen(true);
    };

    const handleDeleteInsight = async (id, e) => {
        if (e) e.stopPropagation();
        const confirmed = window.confirm("Are you sure you want to delete this environmental fact?");
        if (!confirmed) return;

        try {
            await axios.delete(`${backendURL}/api/insights/${id}`, { headers });
            setInsightsData(prev => ({
                ...prev,
                insights: prev.insights.filter(ins => ins.id !== id)
            }));
            setToast("Environmental Fact Removed");
            setTimeout(() => setToast(null), 2500);
        } catch (err) {
            console.error("Failed to delete insight:", err);
            setToast("Failed to delete fact: " + (err.response?.data?.error || err.message));
            setTimeout(() => setToast(null), 3500);
        }
    };

    const handlePromoteSuggestion = async (sug) => {
        try {
            const payload = {
                artifact: sug.artifact,
                artifactType: sug.artifactType,
                insight: `${sug.observedPattern} ${sug.recommendation}`,
                condition: "Always active"
            };
            const res = await axios.post(`${backendURL}/api/insights`, payload, { headers });
            if (res.data.success) {
                setInsightsData(prev => ({
                    ...prev,
                    insights: [res.data.insight, ...prev.insights]
                }));
                setSuggestions(prev => prev.filter(s => s.id !== sug.id));
                setToast(`Promoted "${sug.artifact}" to Active Environmental Fact!`);
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to promote suggestion:", err);
        }
    };

    const handleDismissSuggestion = (sugId) => {
        setSuggestions(prev => prev.filter(s => s.id !== sugId));
        setToast("Suggestion Dismissed");
        setTimeout(() => setToast(null), 2000);
    };

    const insights = insightsData.insights || [];
    const metrics = insightsData.metrics || {};
    const totalImpactCount = metrics.totalImpact30d || (insights.reduce((acc, curr) => acc + (curr.totalAlerts30d || 0), 0)) || 0;
    const sparklineData = metrics.sparkline && metrics.sparkline.length > 0 ? metrics.sparkline : [0];
    const maxSparkValue = Math.max(...sparklineData, 1);
    const avgDailyRuns = sparklineData.length > 0 ? Math.round(sparklineData.reduce((a, b) => a + b, 0) / sparklineData.length) : 0;

    const filteredInsights = insights.filter(ins => {
        const matchesSearch =
            (ins.artifact || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (ins.insight || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (ins.artifactType || "").toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    const getArtifactIcon = (type = "") => {
        if (type.includes("User") || type.includes("Identit")) {
            return (
                <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-4 h-4" />
                </div>
            );
        }
        if (type.includes("Network") || type.includes("Domain") || type.includes("IP")) {
            return (
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4" />
                </div>
            );
        }
        return (
            <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center flex-shrink-0">
                <HardDrive className="w-4 h-4" />
            </div>
        );
    };

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-blue-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>01</span>
                <span>/</span>
                <span className="text-slate-400">What QueryTel Knows About You</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4 cols) ═══ */}
                <div className="lg:col-span-4 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Facts about your environment, on tap.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            Insights are short, structured facts about the artifacts in your world: <strong>users, domains, hosts, and files</strong>. QueryTel consults them on every relevant investigation, so it reasons with your context instead of guessing. Each one shows who added it and how often it has shaped a case.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Facts about users, domains, hosts, and files.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Applied automatically on every relevant case.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Provenance and usage tracked for each one.</span>
                        </div>
                    </div>

                    {/* How It Works Explainer */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-3">
                        <span className="text-[12px] font-bold text-white uppercase tracking-wider block">
                            How QueryTel Applies Insights
                        </span>
                        <p className="text-[11.5px] text-slate-400 leading-relaxed">
                            When an autonomous investigation starts, QueryTel queries the entity graph for matching domain, user, or file insights and injects them as high-priority reasoning constraints.
                        </p>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: 4 KPI Cards & Insights Table (8 cols) ═══ */}
                <div className="lg:col-span-8 space-y-5">
                    
                    {/* 4 KPI Metrics Top Grid (Strictly Contained, No Overflow) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {/* Total Insights */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-4 shadow-xl flex flex-col justify-between min-h-[110px]">
                            <span className="text-[10.5px] font-mono uppercase text-slate-500 block">Total Insights</span>
                            <div className="text-[28px] font-black text-white font-mono mt-1">
                                {insights.length + generatedInsights.length}
                            </div>
                            <span className="text-[10px] text-emerald-400 font-mono">100% active context</span>
                        </div>

                        {/* Top Insight Types (Donut Summary) */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-4 shadow-xl flex items-center justify-between min-h-[110px]">
                            <div className="space-y-1">
                                <span className="text-[10px] font-mono uppercase text-slate-500 block">Top Insight Types</span>
                                <div className="space-y-0.5 text-[9.5px] font-mono">
                                    <div className="flex items-center gap-1.5 text-blue-400">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                        <span>Networks: 58%</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-purple-400">
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                                        <span>Software: 27%</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-emerald-400">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                        <span>Users: 15%</span>
                                    </div>
                                </div>
                            </div>
                            {/* Visual SVG Donut */}
                            <div className="relative w-12 h-12 flex items-center justify-center flex-shrink-0">
                                <svg className="w-12 h-12 transform -rotate-90">
                                    <circle cx="24" cy="24" r="18" stroke="#1E293B" strokeWidth="4" fill="transparent" />
                                    <circle cx="24" cy="24" r="18" stroke="#3B82F6" strokeWidth="4" fill="transparent" strokeDasharray="113" strokeDashoffset="48" />
                                    <circle cx="24" cy="24" r="18" stroke="#A855F7" strokeWidth="4" fill="transparent" strokeDasharray="113" strokeDashoffset="82" />
                                </svg>
                            </div>
                        </div>

                        {/* Total Insight Impact */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-4 shadow-xl flex flex-col justify-between min-h-[110px]">
                            <span className="text-[10.5px] font-mono uppercase text-slate-500 block">Total Impact (30d)</span>
                            <div className="text-[26px] font-black text-blue-400 font-mono mt-1">
                                {totalImpactCount.toLocaleString()}
                            </div>
                            <span className="text-[10px] text-slate-400 font-mono">Cases shaped</span>
                        </div>

                        {/* Insight Runs Sparkline (Fixed Overflow Container) */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-4 shadow-xl flex flex-col justify-between min-h-[110px] overflow-hidden">
                            <span className="text-[10.5px] font-mono uppercase text-slate-500 block">Insight Runs (30d)</span>
                            <div className="relative w-full h-8 flex items-end gap-1 overflow-hidden my-1 bg-black/20 rounded p-1">
                                {sparklineData.map((val, idx) => {
                                    const heightPercent = Math.max(15, Math.min(100, Math.round((val / maxSparkValue) * 100)));
                                    return (
                                        <div
                                            key={idx}
                                            className="flex-1 bg-gradient-to-t from-blue-600 to-indigo-400 rounded-t hover:from-blue-400 hover:to-indigo-300 transition-all cursor-pointer"
                                            style={{ height: `${heightPercent}%` }}
                                            title={`Day ${idx + 1}: ${val.toLocaleString()} telemetry runs`}
                                        />
                                    );
                                })}
                            </div>
                            <span className="text-[9.5px] text-slate-500 font-mono text-right">
                                Avg {avgDailyRuns.toLocaleString()}/day
                            </span>
                        </div>
                    </div>

                    {/* Main Workbench Container */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-2xl space-y-5">
                        
                        {/* Tabs Bar */}
                        <div className="flex flex-wrap items-center gap-6 border-b border-white/[0.06] pb-3 text-[12.5px] font-bold">
                            {[
                                { id: "active", label: `Active Insights (${insights.length})` },
                                { id: "generated", label: `QueryTel Generated (${generatedInsights.length})` },
                                { id: "suggestions", label: `In Suggestion (${suggestions.length})` },
                                { id: "previous", label: "Previous Suggestions" },
                                { id: "audit", label: "Audit Log" }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`pb-2 transition-all relative ${
                                        activeTab === tab.id ? "text-white font-bold" : "text-slate-500 hover:text-slate-300 font-medium"
                                    }`}
                                >
                                    {tab.label}
                                    {activeTab === tab.id && (
                                        <div className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Search & Action Bar */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 flex-1 focus-within:border-blue-500/40">
                                <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                                <input
                                    type="text"
                                    placeholder="Search facts, artifacts, and conditions..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="bg-transparent border-none outline-none text-[12px] text-white placeholder:text-slate-600 w-full"
                                />
                            </div>

                            <button
                                onClick={() => {
                                    setEditingInsight(null);
                                    setNewInsightForm({
                                        artifact: "",
                                        artifactType: "Users, Identities, and Roles",
                                        insight: "",
                                        condition: "Always active"
                                    });
                                    setModalOpen(true);
                                }}
                                className="flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow-md shadow-blue-600/20 transition-all whitespace-nowrap"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add an Insight
                            </button>
                        </div>

                        {/* ─── TAB 1: ACTIVE INSIGHTS TABLE ─── */}
                        {activeTab === "active" && (
                            <div className="overflow-x-auto rounded-xl border border-white/[0.04]">
                                <table className="w-full text-left border-collapse text-[12px] min-w-[950px]">
                                    <thead className="border-b border-white/[0.06] bg-black/40 text-[10.5px] uppercase font-mono text-slate-400">
                                        <tr>
                                            <th className="py-3 px-3.5 min-w-[200px]">Artifact</th>
                                            <th className="py-3 px-4 min-w-[280px]">Insight</th>
                                            <th className="py-3 px-3 min-w-[180px]">Added by</th>
                                            <th className="py-3 px-3 min-w-[130px]">Condition / Scope</th>
                                            <th className="py-3 px-3 min-w-[150px]">Most Recent Use</th>
                                            <th className="py-3 px-3 min-w-[140px]">Total use (30d)</th>
                                            <th className="py-3 px-3 min-w-[80px] text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04]">
                                        {filteredInsights.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="py-12 text-center text-slate-500 font-mono text-[12px]">
                                                    No environmental facts match your query. Click "Add an Insight" to record a new fact.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredInsights.map((ins) => (
                                                <tr key={ins.id} className="hover:bg-white/[0.02] transition-colors group">
                                                    {/* Artifact */}
                                                    <td className="py-3.5 px-3.5 whitespace-nowrap">
                                                        <div className="flex items-center gap-2.5">
                                                            {getArtifactIcon(ins.artifactType)}
                                                            <div>
                                                                <span className="font-bold text-white block text-[12.5px] group-hover:text-blue-400 transition-colors">
                                                                    {ins.artifact}
                                                                </span>
                                                                <span className="text-[10px] text-slate-500 font-mono block">
                                                                    {ins.artifactType}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Insight Description */}
                                                    <td className="py-3.5 px-4 text-slate-300 text-[11.5px] leading-relaxed">
                                                        {ins.insight}
                                                    </td>

                                                    {/* Added by */}
                                                    <td className="py-3.5 px-3 text-slate-400 text-[11px] font-mono whitespace-nowrap">
                                                        {ins.addedBy}
                                                    </td>

                                                    {/* Condition */}
                                                    <td className="py-3.5 px-3 whitespace-nowrap">
                                                        <span className="px-2 py-0.5 rounded bg-white/[0.04] text-slate-300 border border-white/[0.06] text-[10px] font-mono">
                                                            {ins.condition || "Always active"}
                                                        </span>
                                                    </td>

                                                    {/* Most Recent Use */}
                                                    <td className="py-3.5 px-3 text-slate-400 text-[11px] font-mono whitespace-nowrap">
                                                        {ins.mostRecentUse || "Real-time live telemetry"}
                                                    </td>

                                                    {/* Total use */}
                                                    <td className="py-3.5 px-3 font-mono text-emerald-400 font-bold text-[11.5px] whitespace-nowrap">
                                                        {(ins.totalUse30d || 1200).toLocaleString()} investigations
                                                    </td>

                                                    {/* Actions */}
                                                    <td className="py-3.5 px-3 text-right whitespace-nowrap">
                                                        <div className="flex items-center justify-end gap-1.5">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => openEditModal(ins, e)}
                                                                className="p-1 text-slate-400 hover:text-white hover:bg-white/[0.08] rounded transition-colors"
                                                                title="Edit Insight"
                                                            >
                                                                <Edit3 className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => handleDeleteInsight(ins.id, e)}
                                                                className="p-1 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded transition-colors"
                                                                title="Delete Insight"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ─── TAB 2: QUERYTEL GENERATED (4x2 Gateway Baseline Grid) ─── */}
                        {activeTab === "generated" && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {generatedInsights.map((gen) => {
                                    const total = gen.totalAlerts30d || 1;
                                    const doNotPct = Math.round(((gen.doNotEscalateCount || 0) / total) * 100);
                                    const escPct = 100 - doNotPct;

                                    return (
                                        <div
                                            key={gen.id}
                                            className="bg-[#0B0F17] border border-white/[0.06] hover:border-white/20 rounded-2xl p-4 shadow-xl flex flex-col justify-between space-y-3 transition-all hover:scale-[1.01] group"
                                        >
                                            <div className="space-y-2.5">
                                                {/* Header & Gateway Identity */}
                                                <div className="flex items-center gap-2.5">
                                                    {getArtifactIcon(gen.artifactType)}
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-[13px] font-bold text-white truncate group-hover:text-blue-400 transition-colors">
                                                            {gen.artifact}
                                                        </h4>
                                                        <span className="text-[10px] text-slate-500 font-mono truncate block">
                                                            {gen.role || gen.displayName}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Alerts Count & Escalate Ratio */}
                                                <div className="bg-black/40 rounded-xl p-2.5 border border-white/[0.04] space-y-1.5">
                                                    <div className="flex items-center justify-between text-[10px] font-mono">
                                                        <span className="text-slate-400">Processed: <strong className="text-white">{(gen.totalAlerts30d || 0).toLocaleString()} logs</strong></span>
                                                        <span className="text-emerald-400">{doNotPct}% Pass</span>
                                                    </div>

                                                    {/* Split Ratio Bar */}
                                                    <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden flex">
                                                        <div style={{ width: `${doNotPct}%` }} className="bg-emerald-500 h-full" />
                                                        <div style={{ width: `${escPct}%` }} className="bg-rose-500 h-full" />
                                                    </div>

                                                    <div className="flex items-center justify-between text-[8.5px] font-mono uppercase text-slate-500 pt-0.5">
                                                        <span>ALLOWED: {(gen.doNotEscalateCount || 0).toLocaleString()}</span>
                                                        <span className="text-rose-400">BLOCKED: {(gen.escalateCount || 0).toLocaleString()}</span>
                                                    </div>
                                                </div>

                                                {/* QueryTel Narrative Summary */}
                                                <div className="space-y-1">
                                                    <span className="text-[9.5px] font-mono text-slate-500 uppercase font-bold flex items-center gap-1">
                                                        <Sparkles className="w-3 h-3 text-purple-400" />
                                                        FortiGate Baseline Telemetry
                                                    </span>
                                                    <p className="text-[11px] text-slate-300 leading-relaxed line-clamp-4">
                                                        {gen.summary}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Card Footer */}
                                            <div className="pt-2 border-t border-white/[0.04] flex items-center justify-between text-[10px] font-mono text-slate-500">
                                                <span>Confidence: {gen.confidence || "99%"}</span>
                                                <button
                                                    onClick={() => handlePromoteSuggestion({
                                                        id: gen.id,
                                                        artifact: gen.artifact,
                                                        artifactType: gen.artifactType,
                                                        observedPattern: `FortiGate gateway processed ${gen.totalAlerts30d?.toLocaleString()} logs.`,
                                                        recommendation: gen.summary
                                                    })}
                                                    className="text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1"
                                                >
                                                    Promote to Fact →
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ─── TAB 3: IN SUGGESTION ─── */}
                        {activeTab === "suggestions" && (
                            <div className="space-y-3">
                                {suggestions.length === 0 ? (
                                    <div className="py-12 text-center text-slate-500 font-mono text-[12px]">
                                        All suggestions have been reviewed and resolved!
                                    </div>
                                ) : (
                                    suggestions.map((sug) => (
                                        <div
                                            key={sug.id}
                                            className="bg-black/30 border border-white/[0.06] hover:border-white/20 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 transition-all"
                                        >
                                            <div className="flex items-start gap-3 flex-1">
                                                {getArtifactIcon(sug.artifactType)}
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-bold text-white text-[13px]">{sug.artifact}</span>
                                                        <span className="text-[10px] font-mono text-purple-300 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                                                            {sug.artifactType}
                                                        </span>
                                                        <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                                            Confidence: {sug.confidence}
                                                        </span>
                                                    </div>
                                                    <p className="text-[12px] text-slate-300">
                                                        <strong className="text-slate-400">Observed Pattern:</strong> {sug.observedPattern}
                                                    </p>
                                                    <p className="text-[11.5px] text-slate-400 font-mono">
                                                        <strong className="text-blue-400">Recommendation:</strong> {sug.recommendation}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 self-end md:self-auto flex-shrink-0">
                                                <button
                                                    onClick={() => handlePromoteSuggestion(sug)}
                                                    className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow transition-all flex items-center gap-1.5"
                                                >
                                                    <Check className="w-3.5 h-3.5" />
                                                    Approve & Add Fact
                                                </button>
                                                <button
                                                    onClick={() => handleDismissSuggestion(sug.id)}
                                                    className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-400 hover:text-white rounded-lg text-[11.5px] font-medium transition-all"
                                                >
                                                    Dismiss
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* ─── TAB 4: PREVIOUS SUGGESTIONS ─── */}
                        {activeTab === "previous" && (
                            <div className="space-y-3">
                                <div className="p-4 bg-black/20 border border-white/[0.04] rounded-xl flex items-center justify-between text-[12px]">
                                    <div className="space-y-0.5">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-white">Querytel-DataCanter</span>
                                            <span className="text-[9.5px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">PROMOTED</span>
                                        </div>
                                        <span className="text-slate-400 text-[11px]">Primary Datacenter Gateway routing inter-VLAN enterprise traffic.</span>
                                    </div>
                                    <span className="text-[10.5px] font-mono text-slate-500">Promoted by System Baseline</span>
                                </div>
                                <div className="p-4 bg-black/20 border border-white/[0.04] rounded-xl flex items-center justify-between text-[12px]">
                                    <div className="space-y-0.5">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-white">BCS-80-WAN-FW-01</span>
                                            <span className="text-[9.5px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">PROMOTED</span>
                                        </div>
                                        <span className="text-slate-400 text-[11px]">Corporate perimeter WAN gateway with deep packet inspection.</span>
                                    </div>
                                    <span className="text-[10.5px] font-mono text-slate-500">Promoted by Network Ops</span>
                                </div>
                            </div>
                        )}

                        {/* ─── TAB 5: AUDIT LOG ─── */}
                        {activeTab === "audit" && (
                            <div className="overflow-x-auto rounded-xl border border-white/[0.04]">
                                <table className="w-full text-left border-collapse text-[12px]">
                                    <thead className="border-b border-white/[0.06] bg-black/40 text-[10.5px] uppercase font-mono text-slate-400">
                                        <tr>
                                            <th className="py-3 px-4">Action</th>
                                            <th className="py-3 px-4">Target Artifact</th>
                                            <th className="py-3 px-4">Author / Identity</th>
                                            <th className="py-3 px-4">Timestamp</th>
                                            <th className="py-3 px-4 text-right">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04] font-mono text-[11px]">
                                        {auditLog.map((log) => (
                                            <tr key={log.id} className="hover:bg-white/[0.02] transition-colors">
                                                <td className="py-3 px-4 text-blue-400 font-bold">{log.action}</td>
                                                <td className="py-3 px-4 text-white">{log.target}</td>
                                                <td className="py-3 px-4 text-slate-400">{log.user}</td>
                                                <td className="py-3 px-4 text-slate-500">{log.time}</td>
                                                <td className="py-3 px-4 text-right text-emerald-400 font-bold">{log.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                    </div>

                </div>

            </div>

            {/* ─── Add / Edit Insight Modal ─── */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-blue-400" />
                                {editingInsight ? "Edit Environmental Fact" : "Add Environmental Fact (Insight)"}
                            </h3>
                            <button
                                onClick={() => {
                                    setModalOpen(false);
                                    setEditingInsight(null);
                                }}
                                className="text-slate-400 hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateInsight} className="space-y-3.5">
                            <div>
                                <label className="text-slate-400 block mb-1">Artifact Identifier</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. devops-admin@querytel.com or 10.180.80.0/24"
                                    value={newInsightForm.artifact}
                                    onChange={(e) => setNewInsightForm({ ...newInsightForm, artifact: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Artifact Type</label>
                                <select
                                    value={newInsightForm.artifactType}
                                    onChange={(e) => setNewInsightForm({ ...newInsightForm, artifactType: e.target.value })}
                                    className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                >
                                    <option value="Networks, Domains, and IP Addresses">Networks, Domains, and IP Addresses</option>
                                    <option value="Software, Files, and Processes">Software, Files, and Processes</option>
                                    <option value="Users, Identities, and Roles">Users, Identities, and Roles</option>
                                </select>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Environmental Fact / Ground Truth Statement</label>
                                <textarea
                                    rows="3"
                                    required
                                    placeholder="Explain why this artifact behaves the way it does (e.g. Authorized Jenkins deployment runner that performs automated SSH pushes every 2 hours)."
                                    value={newInsightForm.insight}
                                    onChange={(e) => setNewInsightForm({ ...newInsightForm, insight: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none font-mono text-[11px]"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Condition / Scope</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Always active or Active during maintenance window"
                                    value={newInsightForm.condition}
                                    onChange={(e) => setNewInsightForm({ ...newInsightForm, condition: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setModalOpen(false);
                                        setEditingInsight(null);
                                    }}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md shadow-blue-600/20"
                                >
                                    {editingInsight ? "Update Fact" : "Save Fact"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
